'use client';

import { createContext, useContext, type ReactNode } from 'react';

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogScrollArea,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/dialog';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerScrollArea,
  DrawerTitle,
  DrawerTrigger,
} from '~/components/ui/drawer';
import { useMediaQuery } from '~/hooks/use-media-query';
import { MOBILE_BREAKPOINT } from '~/lib/constants';
import { cn } from '~/lib/utils';

// Context to handle media query and open state
const ResponsiveDialogContext = createContext<{
  isDesktop: boolean;
  dismissible: boolean;
}>({
  isDesktop: false,
  dismissible: true,
});

export function ResponsiveDialog({
  children,
  open,
  onOpenChange,
  dismissible = true,
}: {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: ((open: boolean) => void) | undefined;
  dismissible?: boolean;
}) {
  const { value: isMobile } = useMediaQuery(MOBILE_BREAKPOINT);
  const isDesktop = !isMobile;

  return (
    <ResponsiveDialogContext.Provider value={{ isDesktop, dismissible }}>
      {isDesktop ? (
        <Dialog open={open} onOpenChange={onOpenChange}>
          {children}
        </Dialog>
      ) : (
        <Drawer open={open} onOpenChange={onOpenChange} dismissible={dismissible}>
          {children}
        </Drawer>
      )}
    </ResponsiveDialogContext.Provider>
  );
}

export function ResponsiveDialogTrigger({
  children,
  className,
  asChild,
}: {
  children: ReactNode;
  className?: string;
  asChild?: boolean;
}) {
  const { isDesktop } = useContext(ResponsiveDialogContext);

  if (isDesktop) {
    return (
      <DialogTrigger
        className={className}
        asChild={asChild}
        onClick={(e) => {
          e.currentTarget.blur();
        }}
      >
        {children}
      </DialogTrigger>
    );
  }

  return (
    <DrawerTrigger
      className={className}
      asChild={asChild}
      onClick={(e) => {
        e.currentTarget.blur();
      }}
    >
      {children}
    </DrawerTrigger>
  );
}

export function ResponsiveDialogContent({
  children,
  className,
  scrollable = false,
}: {
  children: ReactNode;
  className?: string;
  scrollable?: boolean;
}) {
  const { isDesktop, dismissible } = useContext(ResponsiveDialogContext);

  if (isDesktop) {
    return (
      <DialogContent
        className={cn('sm:max-w-2xl', className)}
        showCloseButton={dismissible}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          if (!dismissible) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          if (!dismissible) {
            e.preventDefault();
          }
        }}
      >
        {scrollable ? <DialogScrollArea>{children}</DialogScrollArea> : children}
      </DialogContent>
    );
  }

  return (
    <DrawerContent className={cn('mx-auto px-5 pb-8 pt-5 sm:max-w-2xl', className)} showDragHandle={dismissible}>
      {scrollable ? <DrawerScrollArea>{children}</DrawerScrollArea> : children}
    </DrawerContent>
  );
}

export function ResponsiveDialogScrollArea({ children, className }: { children: ReactNode; className?: string }) {
  if (useContext(ResponsiveDialogContext).isDesktop) {
    return <DialogScrollArea className={className}>{children}</DialogScrollArea>;
  }

  return <DrawerScrollArea className={className}>{children}</DrawerScrollArea>;
}

export function ResponsiveDialogTitle({
  children,
  className,
  asChild,
}: {
  children: ReactNode;
  className?: string;
  asChild?: boolean;
}) {
  if (useContext(ResponsiveDialogContext).isDesktop) {
    return (
      <DialogTitle className={className} asChild={asChild}>
        {children}
      </DialogTitle>
    );
  }

  return (
    <DrawerTitle className={className} asChild={asChild}>
      {children}
    </DrawerTitle>
  );
}

export function ResponsiveDialogDescription({
  children,
  className,
  asChild,
}: {
  children: ReactNode;
  className?: string;
  asChild?: boolean;
}) {
  if (useContext(ResponsiveDialogContext).isDesktop) {
    return (
      <DialogDescription className={className} asChild={asChild}>
        {children}
      </DialogDescription>
    );
  }

  return (
    <DrawerDescription className={className} asChild={asChild}>
      {children}
    </DrawerDescription>
  );
}

export function ResponsiveDialogFooter({ children, className }: { children: ReactNode; className?: string }) {
  if (useContext(ResponsiveDialogContext).isDesktop) {
    return <DialogFooter className={className}>{children}</DialogFooter>;
  }

  return <DrawerFooter className={className}>{children}</DrawerFooter>;
}

export function ResponsiveDialogClose({ children, className }: { children: ReactNode; className?: string }) {
  if (useContext(ResponsiveDialogContext).isDesktop) {
    return (
      <DialogClose className={className} asChild>
        {children}
      </DialogClose>
    );
  }

  return (
    <DrawerClose className={className} asChild>
      {children}
    </DrawerClose>
  );
}
